import _isNil from 'lodash/isNil';
// Serialization
import { deserialize } from 'serializr';
import { installationAsyncOperationSchema } from './schemas';
// Color
import { statusPalette } from '../components/core/colors';
// Custom
import { SEARCHFIELD_TYPES } from '../constants/App';
import { findInList } from '../utils/Tools';

/**
* Serialization
*/
const deserializeInstallationAsyncOperation = (data) => deserialize(installationAsyncOperationSchema, data);

/**
* Lists
*/
const installationAsyncOperationMethodsList = [
  {
    key: 'get',
    value: 'get',
    labelId: 'asyncOperation.method.get',
  },
  {
    key: 'post',
    value: 'post',
    labelId: 'asyncOperation.method.post',
  },
  {
    key: 'put',
    value: 'put',
    labelId: 'asyncOperation.method.put',
  },
  {
    key: 'delete',
    value: 'delete',
    labelId: 'asyncOperation.method.delete',
  },
];

const installationAsyncOperationResourceTypesList = [
  {
    key: 'allotment',
    value: 'allotment',
    labelId: 'field.allotment',
    resourceKey: 'idAllotment',
  },
  {
    key: 'accessWay',
    permission: 'hidden',
    value: 'access_way',
    labelId: 'field.accessWay',
    resourceKey: 'idAccessWay',
  },
  {
    key: 'antipassback',
    permission: 'hidden',
    value: 'antipassback',
    labelId: 'field.antipassback',
    resourceKey: 'idAntipassback',
  },
  {
    key: 'customer',
    value: 'customer',
    labelId: 'field.customer',
    resourceKey: 'idCustomer',
  },
  {
    key: 'device',
    permission: 'hidden',
    value: 'device',
    labelId: 'field.device',
    resourceKey: 'idDevice',
  },
  {
    key: 'deviceApnMap',
    permission: 'hidden',
    value: 'device_apn_map',
    labelId: 'field.deviceConfig',
    resourceKey: 'idDeviceApnMap',
  },
  {
    key: 'deviceConfig',
    permission: 'hidden',
    value: 'device_config',
    labelId: 'field.deviceConfig',
    resourceKey: 'idDevice',
  },
  {
    key: 'deviceSlave',
    value: 'device_slave',
    labelId: 'field.deviceSlave',
    resourceKey: 'idDeviceSlave',
  },
  {
    key: 'entity',
    value: 'entity',
    labelId: 'field.entity',
    resourceKey: 'idEntity',
  },
  {
    key: 'installation',
    value: 'installation',
    labelId: 'field.installation',
    resourceKey: 'idInstallation',
  },
  {
    key: 'parking',
    value: 'parking',
    labelId: 'field.parking',
    resourceKey: 'idParking',
  },
  {
    key: 'parkingCategory',
    value: 'parking_category',
    labelId: 'field.parkingCategory',
    resourceKey: 'idParkingCategory',
  },
  {
    key: 'planning',
    value: 'planning',
    labelId: 'field.planning',
    resourceKey: 'idPlanning',
  },
  {
    key: 'right',
    permission: 'hidden',
    value: 'right',
    labelId: 'field.right',
    resourceKey: 'idRight',
  },
  {
    key: 'rollout',
    permission: 'hidden',
    value: 'rollout',
    labelId: 'field.rollout',
    resourceKey: 'idRollout',
  },
  {
    key: 'scenario',
    value: 'scenario',
    labelId: 'field.scenario',
    resourceKey: 'idScenario',
  },
  {
    key: 'task',
    value: 'task',
    labelId: 'field.task',
    resourceKey: 'idTask',
  },
];

const installationAsyncOperationStatusesList = [
  {
    key: 'error',
    value: 'error',
    labelId: 'asyncOperation.status.error',
    icon: 'asyncStatusWarning',
    color: statusPalette.warning,
  },
  {
    key: 'pending',
    value: 'pending',
    labelId: 'asyncOperation.status.pending',
    icon: 'asyncStatusPending',
    color: statusPalette.pending,
  },
  {
    key: 'success',
    value: 'success',
    labelId: 'asyncOperation.status.success',
    icon: 'asyncStatusSuccess',
    color: statusPalette.success,
  },
];

const installationAsyncOperationRollbackStatusesList = [
  {
    key: 'committed',
    value: 'committed',
    labelId: 'asyncOperation.rollbackStatus.committed',
    icon: 'asyncRollbackStatusCommitted',
    color: statusPalette.success,
  },
  {
    key: 'pending',
    value: 'pending',
    labelId: 'asyncOperation.rollbackStatus.pending',
    icon: 'asyncRollbackStatusPending',
    color: statusPalette.pending,
  },
  {
    key: 'rollbacked',
    value: 'rollbacked',
    labelId: 'asyncOperation.rollbackStatus.rollbacked',
    icon: 'asyncRollbackStatusRollbacked',
    color: statusPalette.warning,
  },
];

/**
* Utils
*/
const installationAsyncOperationMethod = (asyncOperation) => findInList(installationAsyncOperationMethodsList, 'value', asyncOperation, 'method');
const installationAsyncOperationResourceType = (asyncOperation) => findInList(installationAsyncOperationResourceTypesList, 'value', asyncOperation, 'resource_type');
const installationAsyncOperationStatus = (asyncOperation) => findInList(installationAsyncOperationStatusesList, 'value', asyncOperation, 'status');
const installationAsyncOperationRollbackStatus = (asyncOperation) => findInList(installationAsyncOperationRollbackStatusesList, 'value', asyncOperation, 'rollback_status');
const installationAsyncOperationHasHistory = (asyncOperation) => (asyncOperation.resource_type !== 'installation' && asyncOperation.resource_type !== 'scenario');

/**
* Model
*/
const installationAsyncOperation = {
  // Name and icon
  name: 'installationAsyncOperation',
  icon: null,

  // Serialization
  deserialize: deserializeInstallationAsyncOperation,

  // Lists
  methodsList: installationAsyncOperationMethodsList,
  resourceTypesList: installationAsyncOperationResourceTypesList,
  statusesList: installationAsyncOperationStatusesList,
  rollbackStatusesList: installationAsyncOperationRollbackStatusesList,

  // Utils
  method: installationAsyncOperationMethod,
  resourceType: installationAsyncOperationResourceType,
  status: installationAsyncOperationStatus,
  rollbackStatus: installationAsyncOperationRollbackStatus,
  hasHistory: installationAsyncOperationHasHistory,

  // List features
  listFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [
        { field: 'date', dir: 'desc' },
      ],
    },

    // Columns
    columnsQuantity: () => ({
      xs: 2,
      sm: 3,
      md: 5,
      lg: 7,
      xl: 7,
    }),
    columns: [
      {
        key: 'id',
        labelId: 'field.id',
        field: 'id',
        sortable: false,
        className: 'table-content-1',
      },
      {
        key: 'date',
        labelId: 'field.date',
        field: 'date',
        sortable: false,
        className: 'table-content-4 hidden-xs-down',
      },
      {
        key: 'author',
        labelId: 'field.author',
        field: 'author',
        sortable: false,
        className: 'table-content-1 hidden-md-down',
      },
      {
        key: 'method',
        labelId: 'field.operation',
        field: 'method',
        sortable: false,
        className: 'table-content-2 hidden-sm-down',
      },
      {
        key: 'resource',
        labelId: 'field.resource',
        field: 'resource_type',
        sortable: false,
        className: 'hidden-sm-down',
      },
      {
        key: 'result',
        labelId: 'field.result',
        field: 'status',
        sortable: false,
        className: 'table-content-2 table-content-center',
      },
      {
        key: 'devices',
        labelId: 'field.devices',
        field: 'operation',
        sortable: false,
        className: 'hidden-md-down',
      },
    ],

    // Search fields
    searchFields: [
      {
        key: 'begin',
        labelId: 'field.afterDate',
        field: 'begin',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'after',
        filter: {
          field: 'date',
        },
      },
      {
        key: 'end',
        labelId: 'field.beforeDate',
        field: 'end',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'before',
        filter: {
          field: 'date',
        },
      },
      {
        key: 'resourceType',
        labelId: 'field.resourceType',
        field: 'resourceType',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.enum,
        filter: {
          field: 'resource_type',
        },
      },
      {
        key: 'status',
        labelId: 'field.result',
        field: 'status',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.enum,
        filter: {
          field: 'status',
        },
      },
    ],
  },

  // History list features
  historyListFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [
        { field: 'date', dir: 'desc' },
      ],
    },

    // Columns
    columnsQuantity: () => ({
      xs: 2,
      sm: 3,
      md: 4,
      lg: 6,
      xl: 6,
    }),
    columns: [
      {
        key: 'id',
        labelId: 'field.id',
        field: 'id',
        sortable: false,
        className: 'table-content-1',
      },
      {
        key: 'date',
        labelId: 'field.date',
        field: 'date',
        sortable: false,
        className: 'table-content-4 hidden-xs-down',
      },
      {
        key: 'author',
        labelId: 'field.author',
        field: 'author',
        sortable: false,
        className: 'table-content-1 hidden-md-down',
      },
      {
        key: 'method',
        labelId: 'field.operation',
        field: 'method',
        sortable: false,
        className: 'table-content-2 hidden-sm-down',
      },
      {
        key: 'result',
        labelId: 'field.result',
        field: 'status',
        sortable: false,
        className: 'table-content-2 table-content-center',
      },
      {
        key: 'devices',
        labelId: 'field.devices',
        field: 'operation',
        sortable: false,
        className: 'hidden-md-down',
      },
    ],

    // Search fields
    searchFields: [],
  },
};

module.exports = installationAsyncOperation;



// WEBPACK FOOTER //
// ./models/installationAsyncOperation.js